/**
 * Making table cells fillable by REUSING the standalone element renderers.
 *
 * A table cell hosts a `CellElement` — a typed mini-element whose config is a
 * strict subset of the matching top-level element's config (see table-types).
 * Rather than maintain a second set of fill renderers for in-table elements
 * (which is how `CellElementView` drifted into a static placeholder), we adapt
 * a `CellElement` into a synthetic `PhotoRequirement` and hand it to the very
 * renderers that serve elements outside the table. Behaviour is then identical
 * by construction: same `resolveFillMode` tri-state, same controlled/preview/
 * read-only semantics, same `default_value` / `lock_value` handling.
 *
 * The synthetic requirement is NEVER persisted — it exists only for the render
 * pass. The template still stores the `CellElement`; the worker's answer is
 * stored under `cellFillKey` (below).
 */

import {cellElements} from './cell-elements';
import {
  CELL_SIGNATURE_DEFAULT_HEIGHT,
  type CellElement,
  type CellSignatureConfig,
} from './table-types';
import type {PhotoRequirement, RequirementOf} from './types';

// Row and cell segments are truncated to this many characters (see `cellFillKey`).
const ID_SEGMENT = 8;

/**
 * Address of one fillable cell element within the flat fill-value map.
 *
 * Answers live in the SAME namespace as standalone elements (`FillValues`,
 * `checkout_data`, and a photo attachment's `requirement_code`) rather than
 * nested under the table: every downstream consumer already keys on a flat
 * string id, and the API requires upload tags and value keys to share one
 * namespace.
 *
 * Built from the table element's id plus the row and cell ids and the element's
 * index in the cell's stack. Reordering elements within a cell re-points their
 * answers, which is acceptable: that is an authoring change, not a migration.
 *
 * THE WHOLE TABLE ID IS LOAD-BEARING — do not "simplify" this key. Cell ids are
 * `crypto.randomUUID()`, so it looks like `cellId:index` alone would be unique
 * and the prefix could go. It would not be: `cloneElementsWithGroups`
 * (element-clone.ts) SHALLOW-copies a duplicated element, so a duplicated table
 * gets a fresh element id but SHARES its `config` — and therefore its row and
 * cell ids — with the original. The table id is the only segment that differs
 * between the two, and dropping it would silently cross-wire their answers.
 * `collectCellFillSlots` has a test pinning this.
 *
 * MUST FIT IN 100 CHARACTERS. A photo slot's key becomes the uploaded
 * attachment's `tag`, and `Attachment.tag` is `varchar(100)`, so a key built
 * from three full uuids is 112 chars and the upload is rejected server-side.
 * The row and cell segments are truncated because they only need to be unique
 * WITHIN one table (8 hex chars is 4.3 billion values); the table id stays
 * whole for the reason above.
 */
export function cellFillKey(tableId: string, rowId: string, cellId: string, index: number): string {
  return `${tableId}:${rowId.slice(0, ID_SEGMENT)}:${cellId.slice(0, ID_SEGMENT)}:${index}`;
}

/** The cell-element types that carry a worker-entered answer. */
const FILLABLE_CELL_TYPES = ['text', 'photo', 'checkbox', 'dropdown', 'signature'] as const;

type FillableCellElementType = (typeof FILLABLE_CELL_TYPES)[number];

/** A cell element that carries a worker-entered answer. */
export type FillableCellElement = Extract<CellElement, {type: FillableCellElementType}>;

/**
 * Whether this cell element is a fill slot.
 *
 * `heading` and `logo` are decorative — the same way a standalone `header` or
 * `logo` element is authored content rather than something a crew fills in.
 * They keep rendering through `CellElementView`.
 */
export function isFillableCellElement(el: CellElement): el is FillableCellElement {
  return (FILLABLE_CELL_TYPES as readonly string[]).includes(el.type);
}

/**
 * Whether this cell element must be answered.
 *
 * Only a fill slot can be required, which is also why this narrows rather than
 * reading `config.required` directly: `heading` and `logo` have no such field
 * (`CellHeadingConfig` is `Record<string, never>`), so a plain read needs an
 * `in` guard at every call site and each site gets to pick its own reading of
 * "required". Both canvases ask — the editor's static cell view and the fill
 * canvas — and they have to agree.
 */
export function isRequiredCell(el: CellElement): boolean {
  return isFillableCellElement(el) && el.config.required === true;
}

// A cell is much narrower than the page, so the standalone renderers' page-scale
// defaults (200px photo, 160×64 signature) would overflow a 120px column.
//
// HEIGHTS only. There is no width default to give: an unsized cell element takes
// the width of its column, which is what `CellElementView` draws and what the
// author is actually adjusting when they drag a column edge. A width here said
// otherwise -- 110px flat -- so the same photo was column-wide while being
// designed and 110px on the filled report and in its PDF. `spanWidth` on the
// renderer is where "as wide as the cell" is now expressed.
const CELL_PHOTO_H = 80;

/**
 * Adapt a fillable cell element into the synthetic `PhotoRequirement` the
 * standalone renderers consume.
 *
 * Each `CellXConfig` is the matching standalone config plus an optional
 * `required`, so the config is SPREAD rather than copied field by field. That is
 * the whole point of the adapter: a field added to a cell config reaches the
 * renderer without anyone remembering to extend a copy list here — which is the
 * drift that left in-table elements unfillable in the first place. Only the
 * fields that need a cell-specific default, or that the standalone config
 * requires non-optionally, are named.
 *
 * `style` is empty by design: a cell's own typography and borders come from the
 * `<td>` (`buildCellStyle`), so letting the element restate them would
 * double-apply. `required` is lifted out of the cell config onto the base, where
 * the renderers read it for the `RequiredMark` — it stays in the config too,
 * because `SignatureRenderer` reads it from there first.
 *
 * The overload is what lets callers narrow: every fillable cell type shares its
 * name with the element type it becomes, so the synthetic requirement is exactly
 * `RequirementOf<T['type']>`. Declaring that here means `CellElementFill` can
 * switch and hand each renderer its own variant with no cast at the call site.
 */
export function cellElementToRequirement<T extends FillableCellElement>(
  el: T,
  id: string,
): RequirementOf<T['type']>;
export function cellElementToRequirement(el: FillableCellElement, id: string): PhotoRequirement {
  const base = {
    id,
    label: el.label,
    description: '',
    order: 0,
    style: {},
    position_mode: 'flow' as const,
    required: el.config.required ?? false,
  };
  switch (el.type) {
    case 'text':
      return {...base, type: 'text', config: {...el.config}};
    case 'photo': {
      const c = el.config;
      return {
        ...base,
        type: 'photo',
        config: {...c, height: c.height ?? CELL_PHOTO_H},
      };
    }
    case 'checkbox': {
      const c = el.config;
      return {
        ...base,
        type: 'checkbox',
        config: {...c, options: c.options ?? [], multi_select: c.multi_select ?? false},
      };
    }
    case 'dropdown': {
      const c = el.config;
      return {...base, type: 'dropdown', config: {...c, options: c.options ?? []}};
    }
    case 'signature':
      return {...base, type: 'signature', config: cellSignatureConfig(el.config)};
  }
}

/**
 * A cell signature's config with its authoring defaults filled in.
 *
 * **Read by the design view as well as the fill path, and that is the point.**
 * Signer name + signed-at default ON, matching what a standalone signature gets
 * from `blankRequirement('signature')`; `blankCellElement` authors a cell
 * signature with an EMPTY config, so without these defaults an in-table
 * signature was the one element that silently lost the two lines beneath the
 * pad — the name select and the timestamp.
 *
 * They used to be filled in HERE only, which moved the problem rather than
 * fixing it: the report drew the two lines and the editor's cell view, reading
 * the raw config, drew neither. A JAC was laid out against a signature cell
 * around 22px shorter than the one that printed, so the table it sat in grew on
 * the report and pushed into whatever was placed below it. Same shape as
 * `CELL_SIGNATURE_DEFAULT_HEIGHT`'s own story: a default written on one side of
 * a seam is a default the other side disagrees with.
 */
export function cellSignatureConfig(c: CellSignatureConfig): CellSignatureConfig {
  return {
    ...c,
    show_signer_name: c.show_signer_name ?? true,
    show_signed_at: c.show_signed_at ?? true,
    height: c.height ?? CELL_SIGNATURE_DEFAULT_HEIGHT,
  };
}

/** One fillable cell element, paired with its address in the fill map. */
export interface CellFillSlot {
  key: string;
  element: FillableCellElement;
  /**
   * The ORDER of the table that holds it, so a cell slot sorts among standalone
   * elements in document order rather than being appended after them — the
   * "first missing required field" a crew is sent to has to be the one they see
   * first on the page.
   */
  order: number;
}

/**
 * Every fillable cell element across every table in a template, in document
 * order.
 *
 * The fill UI needs this to do for cell slots what it already does for
 * standalone elements by walking `parseRequirements`: seed authored defaults
 * into the draft, and resolve a photo capture back to the slot that asked for
 * it. Header rows are skipped — they label the table, they don't collect.
 */
export function collectCellFillSlots(requirements: readonly PhotoRequirement[]): CellFillSlot[] {
  const out: CellFillSlot[] = [];
  for (const req of requirements) {
    if (req.type !== 'table') continue;
    for (const {el, row, cell, index} of cellElements(req.config)) {
      // Header rows label the table; they don't collect an answer.
      if (row.isHeader || !isFillableCellElement(el)) continue;
      out.push({key: cellFillKey(req.id, row.id, cell.id, index), element: el, order: req.order});
    }
  }
  return out;
}
